import { z } from 'zod';
import { isoDateTime, likert5, percent } from './common.js';
/**
 * The "things are bad right now" surface: CBT exercises, grounding tools, the
 * SOS flow, and quotes.
 */
export const toolCategory = z.enum([
    'crisis', // right now, cannot think straight
    'cognitive', // thought records, reframing, defusion
    'behavioural', // activation, task initiation
    'grounding', // body and senses
    'emotion', // riding out an intense feeling
    'adhd_executive', // starting, switching, finishing
    'relational', // conflict, RSD, repair
]);
export const logToolSession = z.object({
    toolKey: z.string().min(1).max(60),
    startedAt: isoDateTime.optional(),
    completedAt: isoDateTime.nullable().default(null),
    /** Freeform answers to the tool's promptFields. */
    payload: z.record(z.string(), z.unknown()).default({}),
    distressBefore: percent.nullable().default(null),
    distressAfter: percent.nullable().default(null),
    helpfulness: likert5.nullable().default(null),
});
/**
 * The SOS flow: one button, then a triage question, then the shortest path to
 * something that helps. Logging these matters — over time they show what
 * actually reliably works for *this* person, which is more useful than any
 * generic recommendation.
 */
export const sosPath = z.enum([
    'ground', // 5-4-3-2-1, breathing
    'thought_record',
    'move', // physical discharge
    'talk', // AI companion or a real person
    'crisis_line',
    'just_wait', // urge surfing
]);
export const startSos = z.object({
    distressBefore: percent.nullable().default(null),
    trigger: z.string().max(500).nullable().default(null),
});
export const closeSos = z.object({
    pathTaken: sosPath.nullable().default(null),
    distressAfter: percent.nullable().default(null),
    /**
     * How bad it was when they OPENED it — asked now, at the end.
     *
     * `distressBefore` has been null on every event since the feature shipped,
     * because asking on arrival is worse than not asking: somebody opening this
     * screen is already activated, and a slider between them and the thing that
     * helps costs more than the number is worth.
     *
     * So this is RECALL, and recall is biased — people misremember how bad it
     * was once it has passed, usually toward how they feel now. It therefore
     * has its own column and is never pooled with, averaged into or drawn on
     * the same axis as a contemporaneous reading. Keeping them apart in the
     * schema is what makes that structural instead of remembered.
     */
    distressBeforeRecalled: percent.nullable().default(null),
    note: z.string().max(2000).nullable().default(null),
});
/**
 * The emergency sentence, in one place because it exists in two.
 *
 * The server seeds it with the crisis resources; the web bakes a copy into the
 * bundle for the offline card, which is the one thing in the app that must
 * survive the network being gone. Those two were hand-typed separately and had
 * drifted: the offline copy dropped "or you have taken something", so the
 * version shown when the network is down — a moment at least as likely to
 * matter — omitted the overdose case entirely.
 *
 * Emergency services are named separately from the resource list on purpose,
 * so the list cannot read as though calling 911/999/000 is one option among
 * several when somebody is in immediate danger.
 */
export const EMERGENCY_NOTE = 'If you are in immediate danger, or you have taken something, call your local emergency number now (911 in the US, 999 in the UK, 000 in Australia, 112 across the EU).';
/**
 * The emergency number to dial, per region.
 *
 * The named helplines on the crisis screen have had Call and Text buttons since
 * somebody wrote, in this repo: "reaching help meant reading a sentence,
 * memorising a number, leaving the app and opening the dialer — four steps at
 * the moment executive function is gone." That argument was made about the
 * helplines and never applied to the emergency number sitting above them, which
 * stayed as prose — the four steps, on the one line that matters most.
 *
 * `null` where the number cannot be known from the region alone. A button that
 * dials the wrong emergency number is worse than a sentence naming four.
 */
export const EMERGENCY_DIAL = {
    US: '911',
    CA: '911',
    UK: '999',
    AU: '000',
    INTL: null,
};
//# sourceMappingURL=support.js.map